<template>
  <div class="main-content consensus-page">
    <div class="container">
      <div class="header-section">
        <!-- <div class="back-link"><a href="/#/singleCell/list"><i class="el-icon-back"></i> Per Dataset View</a></div> -->
        <h1>Consensus Score</h1>
        <p class="lead">Explore cross-dataset gene coexpression consensus within each organism, tissue and cell type.</p>
      </div>

      <section class="panel selector-panel">
        <div class="panel-heading">
          <div><span class="eyebrow">CONSENSUS EXPLORER</span><h2>Select a context</h2></div>
          <span class="hint">Choose from the top down to keep available values in context.</span>
        </div>
        <el-row :gutter="18">
          <el-col :span="24">
            <label>Organism</label>
            <el-select v-model="filters.organism" placeholder="Select organism" clearable @change="handleOrganismChange">
              <el-option v-for="item in organisms" :key="item" :label="item" :value="item" />
            </el-select>
          </el-col>
          <el-col :span="24">
            <label>Tissue</label>
            <el-select v-model="filters.tissue" placeholder="Select tissue" clearable :disabled="!filters.organism" @change="handleTissueChange">
              <el-option v-for="item in tissues" :key="item" :label="item" :value="item" />
            </el-select>
          </el-col>
          <el-col :span="24">
            <label>Cell Type</label>
            <el-select v-model="filters.cellType" placeholder="Select cell type" clearable :disabled="!filters.tissue" @change="handleCellTypeChange">
              <el-option v-for="item in cellTypes" :key="item" :label="item" :value="item" />
            </el-select>
          </el-col>
        </el-row>
        <div class="selector-actions">
          <el-button
            type="primary"
            icon="el-icon-search"
            :loading="loading"
            :disabled="!filters.organism || !filters.tissue || !filters.cellType"
            @click="queryConsensus"
          >
            Query
          </el-button>
        </div>
      </section>

      <section v-if="filters.cellType" class="panel results-panel">
        <div class="panel-heading results-heading">
          <div><span class="eyebrow">GENE PAIRS</span><h2>{{ filters.cellType }} consensus results</h2></div>
          <el-button type="primary" icon="el-icon-download" :disabled="!rows.length" @click="downloadResults">Download</el-button>
        </div>
        <div class="filter-line">
          <el-select v-model="gene1" filterable clearable placeholder="Filter Gene 1" @change="search">
            <el-option v-for="item in geneOptions" :key="item" :label="item" :value="item" />
          </el-select>
          <span class="result-note">Showing the highest consensus scores first. 20 records per page by default.</span>
        </div>
        <el-table v-loading="loading" :data="rows" stripe border empty-text="Select a context to view consensus scores">
          <el-table-column v-for="column in columns" :key="column" :prop="column" :label="formatColumnLabel(column)" min-width="150" show-overflow-tooltip />
        </el-table>
        <div class="pagination-wrapper">
          <el-pagination layout="total, sizes, prev, pager, next" :total="total" :current-page="pageNum" :page-size="pageSize" :page-sizes="[20, 50, 100]" @size-change="handleSizeChange" @current-change="handleCurrentChange" />
        </div>
      </section>
      <div v-else class="empty-state"><i class="el-icon-data-analysis"></i><h3>Choose organism, tissue and cell type</h3><p>The consensus score table will appear here.</p></div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'SingleCellConsensus',
  data() {
    return {
      organisms: ['Human', 'Mouse'], tissues: [], cellTypes: [], geneOptions: [], rows: [], columns: [], total: 0,
      loading: false, gene1: '', pageNum: 1, pageSize: 20,
      filters: { organism: '', tissue: '', cellType: '' }
    };
  },
  created() { this.loadOrganisms(); },
  methods: {
    async loadOrganisms() {
      try { const res = await this.$ajax.get('singleCell/consensus/organisms'); this.organisms = res.data || this.organisms; } catch (e) { /* endpoint may be deployed with the next server release */ }
    },
    async handleOrganismChange() {
      this.filters.tissue = ''; this.filters.cellType = ''; this.tissues = []; this.cellTypes = []; this.rows = []; this.columns = [];
      if (!this.filters.organism) return;
      try { const res = await this.$ajax.get('singleCell/consensus/tissues', { organism: this.filters.organism }); this.tissues = res.data || []; } catch (e) { this.$message.info('Tissue options are not available yet.'); }
    },
    async handleTissueChange() {
      this.filters.cellType = ''; this.cellTypes = []; this.rows = []; this.columns = [];
      if (!this.filters.tissue) return;
      try { const res = await this.$ajax.get('singleCell/consensus/cellTypes', { organism: this.filters.organism, tissue: this.filters.tissue }); this.cellTypes = res.data || []; } catch (e) { this.$message.info('Cell type options are not available yet.'); }
    },
    async handleCellTypeChange() {
      this.pageNum = 1;
      this.gene1 = '';
      this.rows = [];
      this.columns = [];
      this.geneOptions = [];
    },
    async queryConsensus() {
      if (!this.filters.organism || !this.filters.tissue || !this.filters.cellType) return;
      this.pageNum = 1;
      await this.search();
    },
    async search() {
      if (!this.filters.cellType) return;
      this.loading = true;
      try {
        const params = { ...this.filters, gene1: this.gene1 || undefined, pageNum: this.pageNum, pageSize: this.pageSize };
        const res = await this.$ajax.get('singleCell/consensus/page', params);
        const page = res.data || {};
        this.rows = page.records || page.rows || [];
        this.columns = page.columns || (this.rows.length ? Object.keys(this.rows[0]) : []);
        this.total = page.total || this.rows.length;
        this.geneOptions = page.geneOptions || this.geneOptions;
      } catch (e) { this.$message.warning('Consensus score data is currently unavailable.'); }
      finally { this.loading = false; }
    },
    handleSizeChange(size) {
      this.pageSize = Number(size);
      this.pageNum = 1;
      this.search();
    },
    handleCurrentChange(page) { this.pageNum = page; this.search(); },
    formatColumnLabel(column) {
      return String(column).replace(/[_-]+/g, ' ').replace(/([a-z])([A-Z])/g, '$1 $2').replace(/\b\w/g, value => value.toUpperCase());
    },
    downloadResults() {
      const query = new URLSearchParams({ ...this.filters, gene1: this.gene1 || '' }).toString();
      window.open(`/api/singleCell/consensus/export?${query}`, '_blank');
    }
  }
};
</script>

<style scoped>
.main-content { background: var(--bg); min-height: 100vh; padding: 30px 0 56px; font-family: 'Inter', sans-serif; color: var(--navy); }
.container { max-width: 1180px; margin: 0 auto; padding: 0 20px; }
.header-section { margin-bottom: 30px; text-align: center; }
.header-section h1 { margin: 8px 0 10px; font-family: 'Playfair Display', serif; font-size: 42px; color: var(--navy); }
.lead { max-width: 640px; margin: 0 auto; color: var(--muted); font-size: 18px; line-height: 1.6; }
.panel { padding: 30px; background: linear-gradient(180deg, #f9fbfe 0%, #d8e1ee 100%); border: 1px solid var(--border); border-radius: 20px; box-shadow: var(--shadow); }
.selector-panel .el-col { margin-bottom: 18px; }
.selector-panel .el-col:last-child { margin-bottom: 0; }
.selector-actions { display: flex; justify-content: center; margin-top: 24px; padding-top: 22px; border-top: 1px solid rgba(92, 112, 139, 0.18); }
.selector-panel, .results-panel { margin-bottom: 24px; }
.panel-heading { display: flex; justify-content: space-between; align-items: flex-start; gap: 20px; margin-bottom: 24px; }
.panel-heading h2 { margin: 4px 0 0; color: var(--navy); }
.eyebrow { color: var(--accent); font-size: 11px; font-weight: 700; letter-spacing: 1.2px; }
.hint, .result-note { color: var(--muted); font-size: 13px; }
label { display: block; color: var(--navy); font-size: 13px; font-weight: 700; margin-bottom: 8px; }
.el-select { width: 100%; }
.results-heading { align-items: center; }
.filter-line { display: flex; align-items: center; gap: 14px; margin-bottom: 16px; }
.filter-line .el-select { width: 260px; }
.pagination-wrapper { display: flex; justify-content: flex-end; align-items: center; margin-top: 24px; padding-top: 18px; border-top: 1px solid rgba(92, 112, 139, 0.16); min-height: 34px; }
.pagination-wrapper .el-pagination { padding: 0; }
.empty-state { text-align: center; padding: 80px 20px; color: var(--muted); }
.empty-state i { font-size: 42px; color: var(--accent); }
.empty-state h3 { color: var(--navy); margin: 16px 0 8px; }
@media (max-width: 720px) { .panel { padding: 22px 18px; } .panel-heading, .filter-line { flex-direction: column; align-items: stretch; } .filter-line .el-select { width: 100%; } .selector-actions, .pagination-wrapper { justify-content: center; } .pagination-wrapper { overflow-x: auto; } }
</style>
